import { useState } from 'react';
import { CallStatus, useCallable } from '../lib/useCallable';

/**
 * Finding somebody in Entra so their roster record is not typed out by hand.
 *
 * WHY SEARCH RATHER THAN A BULK IMPORT. The roster is the list of people who use this app, not a
 * copy of the firm's directory. Pulling in every enabled account would fill it with people who
 * will never open it and make every people-chooser in the app longer for no gain. Searching keeps
 * it deliberate while removing the actual problem, which is somebody retyping a name and an email
 * and getting one of them slightly wrong.
 *
 * IT FILLS THE FORM, IT DOES NOT SAVE. Clicking a result opens the ordinary editor with the name,
 * address and title filled in — the roles and practice groups are still chosen by a person,
 * because those decide who may approve a matter and the directory has no opinion worth trusting
 * on that. A job title is a description of somebody's job; a role here is a permission.
 *
 * ALREADY-ON-THE-ROSTER IS SHOWN, NOT HIDDEN. Somebody searching for a colleague who is already
 * there should be told so, rather than seeing no result and concluding the lookup is broken.
 */
export default function DirectoryLookup({ existingEmails, onPick }) {
  const { run, busy, error } = useCallable();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [truncated, setTruncated] = useState(false);

  const search = async () => {
    setResults(null);
    const data = await run('adminSearchDirectoryUsers', { query, domain: 'aalrr.com' });
    if (data) {
      setResults(data.users || []);
      setTruncated(Boolean(data.truncated));
    }
  };

  const known = new Set((existingEmails || []).map((e) => String(e || '').toLowerCase()));

  return (
    <section className="rounded-lg bg-white p-4 shadow dark:bg-slate-900">
      <div className="flex items-center gap-3">
        <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Find somebody in the directory</h3>
        <CallStatus busy={busy} error={error} saved={false} />
      </div>
      <p className="mt-1 text-xs text-slate-600 dark:text-slate-300">
        Search Entra by name or address and start their roster record from it, so the name and email
        are not retyped. Roles and practice groups are still yours to choose.
      </p>

      <div className="mt-3 flex gap-2">
        <input
          className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm dark:border-slate-600 dark:bg-slate-900"
          value={query}
          placeholder="Surname, first name, or email"
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') search();
          }}
        />
        <button
          type="button"
          onClick={search}
          disabled={busy || query.trim().length < 2}
          className="shrink-0 rounded border border-slate-300 px-3 py-1.5 text-sm disabled:opacity-40 dark:border-slate-600"
        >
          Search
        </button>
      </div>

      {results && results.length === 0 && (
        <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">Nobody in the directory matches that.</p>
      )}

      {results && results.length > 0 && (
        <ul className="mt-3 divide-y divide-slate-100 dark:divide-slate-800">
          {results.map((person) => {
            const already = known.has(person.email);
            return (
              <li key={person.email} className="flex items-center gap-3 py-2">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm text-ink dark:text-slate-100">
                    {person.displayName}
                    {!person.accountEnabled && (
                      <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-[11px] text-amber-900 dark:bg-amber-950 dark:text-amber-200">
                        account disabled
                      </span>
                    )}
                  </p>
                  <p className="truncate text-xs text-slate-500 dark:text-slate-400">
                    {person.email}
                    {person.jobTitle ? ` · ${person.jobTitle}` : ''}
                    {person.department ? ` · ${person.department}` : ''}
                  </p>
                </div>
                {already ? (
                  <span className="shrink-0 text-xs text-slate-400">Already on the roster</span>
                ) : (
                  <button
                    type="button"
                    onClick={() => onPick(person)}
                    className="shrink-0 rounded bg-firm-700 px-3 py-1 text-xs font-medium text-white hover:bg-firm-900 dark:bg-firm-500 dark:hover:bg-firm-400"
                  >
                    Start their record
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {truncated && (
        <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
          More people match than are shown. Narrow the search.
        </p>
      )}
    </section>
  );
}
